import StationListSectionHeader from "components/layout/StationListSectionHeader";
import { frequentStations, popularRoutes } from "const/linksData";
import { useCallback, useState } from "react";
import { TrainResponse } from "services/trainService";
import { useIntl } from "react-intl";
import QuickLinkList from "./QuickLinkList";
import SearchBar from "./SearchBar";
import { StorageUtils } from "utils/storageUtils";
import { useGetStationsQuery } from "app/apiSlice";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "components/LoadingSpinner";
import { WithRequired } from "types/utilityTypes";
import CardLayout from "components/layout/CardLayout";

export type SearchSection =
  | "recentSearches"
  | "popularRoutes"
  | "frequentStations"
  | "apiStations";

export type LinkItem = WithRequired<
  Partial<
    TrainResponse & {
      arrivalCode: string;
      departureCode: string;
      path: string;
    }
  >,
  "stationName"
>;

/**
 * Checks if the given item is a LinkItem.
 *
 * @param {LinkItem | TrainResponse} item - The item to check.
 * @return {item is LinkItem} - True if the item is a LinkItem, false otherwise.
 */
const isLink = (item: LinkItem | TrainResponse): item is LinkItem => {
  if ("arrivalCode" in item) {
    return true;
  }
  return false;
};

export type StationOption = "departure" | "arrival";

interface QuickLinksProps {
  stationOption: StationOption;
  closeWindow: () => void;
}

const QuickLinks = ({ stationOption, closeWindow }: QuickLinksProps) => {
  // hooks
  const intl = useIntl();
  const navigate = useNavigate();

  // state
  // Using a query hook automatically fetches data and returns query values
  const {
    data: stations = [],
    isLoading,
    isError,
    error,
  } = useGetStationsQuery();
  // Individual hooks are also accessible under the generated endpoints:
  // const { data, error, isLoading } = apiSlice.endpoints.getStations.useQuery();

  const [recentSearchesState, setRecentSearchesState] = useState<
    TrainResponse[]
  >(JSON.parse(StorageUtils.RecentSearches.get() || "[]"));

  // effect
  const saveToRecentSearches = useCallback(
    (link: TrainResponse) => {
      const index = recentSearchesState.findIndex(
        (item) => item.stationCode === link.stationCode
      );

      if (index > -1) {
        // Station is already in the list
        setRecentSearchesState((prev) => {
          prev.splice(index, 1);
          const current = [link, ...prev];
          StorageUtils.RecentSearches.set(JSON.stringify(current));

          return current;
        });
      } else {
        // Station is not in the list
        setRecentSearchesState((prev) => {
          const current = [link, ...prev].slice(0, 6);
          StorageUtils.RecentSearches.set(JSON.stringify(current));

          return current;
        });
      }
    },
    [recentSearchesState]
  );

  const clearRecentSearches = () => {
    StorageUtils.RecentSearches.remove();
    setRecentSearchesState([]);
  };

  const handleLinkClick = useCallback(
    (link: TrainResponse | LinkItem) => {
      const params = new URLSearchParams(window.location.search);
      const arrivalCode = params.get("arrivalCode");
      const departureCode = params.get("departureCode");

      // 인기 노선 선택 처리
      if (isLink(link)) {
        const [departure, arrival] = link.stationName.split(" → ");
        const { departureCode, arrivalCode } = link;

        params.set("departure", departure);
        params.set("arrival", arrival);
        params.set("departureCode", departureCode!);
        params.set("arrivalCode", arrivalCode!);

        navigate(`?${params.toString()}`);
        closeWindow();
        return;
      }

      // 출발역과 도착역과 같은지 확인
      if (
        (stationOption === "departure" && link.stationCode === arrivalCode) ||
        (stationOption === "arrival" && link.stationCode === departureCode)
      ) {
        alert(
          intl.formatMessage({ id: "stationSelectionPopup.sameStationError" })
        );
        return;
      }

      saveToRecentSearches(link);

      // 기타 링크 선택 처리
      params.set(`${stationOption}`, link.stationName);
      params.set(`${stationOption}Code`, link.stationCode);

      navigate(`?${params.toString()}`);
      closeWindow();
    },
    [closeWindow, intl, navigate, saveToRecentSearches, stationOption]
  );

  if (isError) {
    alert("역 목록을 가져오는데 실패했습니다.");
    console.error("역 목록을 가져오는데 실패했습니다.", error);
    navigate("/");
  }

  return (
    <div className="relative mx-auto pt-10">
      <SearchBar
        stations={stations}
        stationOption={stationOption}
        saveToRecentSearches={saveToRecentSearches}
        closeWindow={closeWindow}
      />
      {isLoading ? (
        <LoadingSpinner overlap />
      ) : (
        <div className="mb-2 space-y-3">
          {/* Recent search */}
          {recentSearchesState.length > 0 && (
            <CardLayout className="p-0">
              <StationListSectionHeader
                id="recentlySearched"
                onClick={clearRecentSearches}
              />
              <QuickLinkList
                items={recentSearchesState}
                section="recentSearches"
                handleLinkClick={handleLinkClick}
              />
            </CardLayout>
          )}

          {/* Popular routes */}
          <CardLayout className="p-0">
            <StationListSectionHeader id="popularRoutes" />
            <QuickLinkList
              items={popularRoutes}
              section="popularRoutes"
              handleLinkClick={handleLinkClick}
              gridColumn={1}
            />
          </CardLayout>

          {/* Frequent stations */}
          <CardLayout className="p-0">
            <StationListSectionHeader id="frequentlyVisited" />
            <QuickLinkList
              items={frequentStations}
              section="frequentStations"
              handleLinkClick={handleLinkClick}
            />
          </CardLayout>

          {/* all stations */}
          <CardLayout className="p-0">
            <StationListSectionHeader id="allStations" />
            <QuickLinkList
              items={stations}
              section="apiStations"
              handleLinkClick={handleLinkClick}
            />
          </CardLayout>
        </div>
      )}
    </div>
  );
};

export default QuickLinks;
